import type { SessionConfigSelect } from '@agentclientprotocol/sdk/experimental/v2'
import { AssistantRuntimeProvider } from '@assistant-ui/react'
import {
  BridgeProvider,
  useBridge,
  useBridgeActions,
  useBridgeRuntime,
  useSnapshot,
} from '@web/adapter/useBridgeRuntime'
import { api } from '@web/api/client'
import type {
  AppDefinition,
  SessionConfig,
  SessionRecord,
} from '@web/api/types'
import { AcpControls } from '@web/components/AcpControls'
import { AppPane } from '@web/components/AppPane'
import { Row, RowGroup, Sheet } from '@web/components/mobile/Sheet'
import { CircleButton, TopBar } from '@web/components/mobile/TopBar'
import { RenameSession } from '@web/components/RenameSession'
import {
  RepositoryDialog,
  repositoryCheckout,
} from '@web/components/RepositoryPicker'
import {
  openSessionArtifact,
  SessionArtifacts,
} from '@web/components/SessionArtifacts'
import { SessionCost } from '@web/components/SessionCost'
import { SessionDetailsPanel } from '@web/components/SessionDetailsPanel'
import {
  DesktopSessionChrome,
  SessionHeading,
} from '@web/components/SessionHeading'
import { SessionLinks } from '@web/components/SessionLinks'
import { SessionSharing } from '@web/components/SessionSharing'
import { SessionWorkspaceSkeleton } from '@web/components/SessionSkeleton'
import { StatusBar } from '@web/components/StatusBar'
import { Thread } from '@web/components/Thread'
import { Icon } from '@web/components/ui/icons'
import { ENABLE_SESSION_SHARING } from '@web/features'
import { executionTitle } from '@web/lib/executionMode'
import { agentMetadata } from '@web/state/acp'
import {
  appConfiguration,
  useAppPreference,
} from '@web/state/app-configuration'
import { navigateBack, useIdentity } from '@web/state/identity'
import { getSessionStore } from '@web/state/session-store'
import { describeWork, effectiveStatus } from '@web/state/working'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'

type Props = {
  app: AppDefinition
  /** Phone layout: flat header with an options menu, settings in a sheet. */
  narrow?: boolean
  sessionId: string
  record: SessionRecord | null
  infoOpen: boolean
  onToggleInfo: () => void
  onSessionsChanged: () => void
  onSessionTitleChanged: (record: SessionRecord) => void
  onClosed: () => void
  onFork: (preset: Partial<SessionConfig>) => void
}

/** The negotiated model, as the agent names it, for the phone header. */
function currentModel(
  options: SessionConfigSelect[] | unknown[],
): string | null {
  const option = (
    options as { type: string; configId: string; name: string }[]
  ).find(
    (item) =>
      item.type === 'select' &&
      (item.configId === 'model' || /model/i.test(item.name)),
  ) as SessionConfigSelect | undefined
  if (!option) return null
  const choices = option.options.flatMap((item) =>
    'options' in item ? item.options : [item],
  )
  return (
    choices.find((choice) => choice.value === option.currentValue)?.name ?? null
  )
}

export function SessionView(props: Props) {
  return appConfiguration(props.app, props.record).ui.mode === 'ui' ? (
    <UiSessionView {...props} />
  ) : (
    <ChatSessionView {...props} />
  )
}

function UiSessionView(props: Props) {
  const store = useMemo(
    () => getSessionStore(props.sessionId, props.record ?? undefined),
    [props.sessionId, props.record],
  )
  const snapshot = useSnapshot(store)
  const actions = useBridgeActions(store, snapshot)
  return (
    <BridgeProvider value={actions}>
      <SessionContent {...props} />
    </BridgeProvider>
  )
}

function ChatSessionView(props: Props) {
  const store = useMemo(
    () => getSessionStore(props.sessionId, props.record ?? undefined),
    [props.sessionId, props.record],
  )
  const { runtime, actions } = useBridgeRuntime(store)
  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <BridgeProvider value={actions}>
        <SessionContent {...props} />
      </BridgeProvider>
    </AssistantRuntimeProvider>
  )
}

function SessionContent({
  app,
  narrow = false,
  sessionId,
  record,
  infoOpen,
  onToggleInfo,
  onSessionsChanged,
  onSessionTitleChanged,
  onClosed,
  onFork,
}: Props) {
  const identity = useIdentity()
  const desktopChrome = useContext(DesktopSessionChrome)
  const actions = useBridge()
  const { snapshot } = actions
  const { acp, bridge } = snapshot
  const rec = bridge.record ?? record
  const archived = record?.status === 'archived' || rec?.status === 'archived'
  const shared = !!rec && rec.owner_user_id !== identity.state?.current_user?.id
  const metadata = agentMetadata(snapshot.acp)
  const hasWorkspace = metadata.workspace === true
  const configuration = appConfiguration(app, rec)
  const { ui } = configuration
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [renaming, setRenaming] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [repositoryOpen, setRepositoryOpen] = useState(false)
  const [minimized, setMinimized] = useAppPreference(
    app.id,
    'pane-minimized',
    ui.minimized,
  )
  const [width, setWidth] = useAppPreference(
    app.id,
    'pane-width',
    ui.width_percent,
  )
  const [tab, setTab] = useState<'chat' | 'ui'>(ui.minimized ? 'chat' : 'ui')
  useEffect(() => {
    const discuss = (event: Event) => {
      if (
        (event as CustomEvent<{ sessionId: string }>).detail?.sessionId ===
        sessionId
      )
        setTab('chat')
    }
    window.addEventListener('bridge:factory-draft', discuss)
    return () => window.removeEventListener('bridge:factory-draft', discuss)
  }, [sessionId])

  useEffect(() => {
    const open = (event: Event) => {
      if (
        (event as CustomEvent<{ sessionId: string }>).detail?.sessionId ===
        sessionId
      )
        setSettingsOpen(true)
    }
    window.addEventListener('bridge:session-settings', open)
    return () => window.removeEventListener('bridge:session-settings', open)
  }, [sessionId])

  const row = useRef<HTMLDivElement>(null)
  const owner = identity.state?.authorized_users.find(
    (user) => user.id === rec?.owner_user_id,
  )
  const status = effectiveStatus(bridge, acp)
  const work = describeWork(acp, bridge)
  const terminal = ['exited', 'error', 'detached', 'asleep', 'closed'].includes(
    status,
  )
  const showChat = ui.mode !== 'ui'
  const showPane = ui.mode !== 'chat' && !shared
  const title = rec?.title ?? 'Session'
  const implicit = configuration.sessions.nature === 'implicit'
  const versionedApp = {
    ...app,
    digest: rec?.config.package_digest ?? app.digest,
    descriptor: { ...app.descriptor, configuration },
  }

  useEffect(() => {
    if (
      rec &&
      (rec.title !== record?.title ||
        rec.title_manual !== record?.title_manual ||
        rec.title_provisional !== record?.title_provisional)
    )
      onSessionTitleChanged(rec)
  }, [
    onSessionTitleChanged,
    rec,
    rec?.title,
    rec?.title_manual,
    rec?.title_provisional,
    record?.title,
    record?.title_manual,
    record?.title_provisional,
  ])

  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (
        event.key === 'Escape' &&
        showChat &&
        !shared &&
        (status === 'running' || status === 'requires_action')
      )
        actions.interrupt()
    }
    window.addEventListener('keydown', key)
    return () => window.removeEventListener('keydown', key)
  }, [status, actions, showChat, shared])
  useEffect(() => {
    onSessionsChanged()
  }, [bridge.status, acp.running, onSessionsChanged])
  useEffect(() => {
    const inspect = (event: Event) => {
      const detail = (event as CustomEvent<{ bindingId?: string }>).detail
      if (
        !showPane ||
        !detail ||
        detail.bindingId !== rec?.config.app_binding_id
      )
        return
      setMinimized(false)
      setTab('ui')
    }
    window.addEventListener('bridge:analysis-inspect', inspect)
    window.addEventListener('bridge:inspect', inspect)
    return () => {
      window.removeEventListener('bridge:analysis-inspect', inspect)
      window.removeEventListener('bridge:inspect', inspect)
    }
  }, [rec?.config.app_binding_id, showPane])

  const run = async (name: string, fn: () => Promise<unknown>) => {
    setBusy(name)
    setError('')
    try {
      await fn()
      onSessionsChanged()
    } catch (failure) {
      setError((failure as Error).message)
    } finally {
      setBusy(null)
    }
  }
  const resize = (clientX: number) => {
    const bounds = row.current?.getBoundingClientRect()
    if (!bounds) return
    const left = ((clientX - bounds.left) / bounds.width) * 100
    setWidth(
      Math.round(
        Math.min(80, Math.max(20, ui.position === 'left' ? left : 100 - left)),
      ),
    )
  }
  const restorePane = () => {
    setMinimized(false)
    setTab('ui')
  }
  const navigateTab = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
    event.preventDefault()
    const next =
      event.key === 'Home'
        ? 'chat'
        : event.key === 'End'
          ? 'ui'
          : tab === 'chat'
            ? 'ui'
            : 'chat'
    if (next === 'ui') restorePane()
    else setTab('chat')
    document.getElementById(`session-${next}-tab`)?.focus()
  }

  const executionLabel =
    (rec?.config.execution_mode ?? metadata.executionMode) === 'claude-managed'
      ? executionTitle('claude-managed', rec?.config.provider)
      : (rec?.config.execution_mode ?? metadata.executionMode) ===
          'tool-restricted'
        ? executionTitle('tool-restricted')
        : implicit
          ? 'Your workspace'
          : rec?.project || null
  const forkPreset = () =>
    rec &&
    onFork({
      ...rec.config,
      provider_session_id: undefined,
      title: null,
      fork_session: true,
      source_session_id: rec.id,
    })
  const showFork =
    ui.mode !== 'ui' &&
    rec?.config.execution_mode === 'docker' &&
    !!acp.capabilities.session?.fork
  const canAttachRepository =
    !shared &&
    !archived &&
    ['docker', 'kubernetes'].includes(rec?.config.execution_mode ?? '') &&
    ['ready', 'idle', 'running', 'requires_action'].includes(status) &&
    (rec?.config.repositories?.length ?? 0) < 8
  const detailsModel =
    currentModel(acp.configOptions) ?? rec?.model ?? rec?.config.model ?? null
  const archive = () =>
    void run('archive', async () => {
      await api.archiveSession(sessionId)
      onClosed()
    })
  return (
    <div
      className={`app-session mode-${ui.mode} ${narrow ? 'm-session relative' : ''}`}
    >
      {narrow ? (
        <TopBar
          title={implicit ? app.title : title}
          subtitle={[
            implicit ? null : work.label,
            executionLabel,
            currentModel(acp.configOptions),
          ]
            .filter(Boolean)
            .join(' · ')}
          onBack={() => navigateBack(implicit ? '/' : `/apps/${app.id}`)}
          right={
            <CircleButton
              icon="more"
              label="Session options"
              expanded={menuOpen}
              onClick={() => setMenuOpen(true)}
            />
          }
        />
      ) : (
        <SessionHeading
          identity={
            <div
              className={`${desktopChrome ? '' : 'session-heading-copy '}min-w-0 flex-1`}
            >
              <div className="session-heading-title flex items-center gap-2 min-w-0">
                {!implicit && !desktopChrome && (
                  <>
                    <span
                      className="session-heading-app truncate"
                      title={app.title}
                    >
                      {app.title}
                    </span>
                    <span
                      className="session-heading-divider"
                      aria-hidden="true"
                    >
                      /
                    </span>
                  </>
                )}
                <span
                  title={implicit ? app.title : title}
                  className={`truncate text-[13.5px] font-semibold ${rec?.title_provisional ? 'provisional-title' : ''}`}
                >
                  {implicit ? app.title : title}
                </span>
                {!implicit && !shared && (
                  <button
                    className="btn btn-icon shrink-0"
                    aria-label="Rename session"
                    onClick={() => setRenaming(true)}
                  >
                    <Icon name="edit" size={14} />
                  </button>
                )}
              </div>
            </div>
          }
        >
          {ENABLE_SESSION_SHARING && !shared && showChat && (
            <SessionSharing sessionId={sessionId} />
          )}
          {shared && (
            <span className="session-readonly">Shared · Read only</span>
          )}
          {!shared && <SessionArtifacts sessionId={sessionId} />}
          {canAttachRepository && (
            <button
              className="btn"
              disabled={!!busy}
              onClick={() => setRepositoryOpen(true)}
            >
              <Icon
                name={busy === 'attach-repository' ? 'loader' : 'repo'}
                size={15}
              />
              {busy === 'attach-repository'
                ? 'Adding repository…'
                : 'Add repository'}
            </button>
          )}
          <button
            type="button"
            className="btn session-details-trigger"
            aria-label="Session details"
            aria-expanded={infoOpen}
            onClick={onToggleInfo}
          >
            <Icon name="info" size={15} />
            Session
          </button>
          {ui.mode === 'hybrid' && !shared && (
            <button
              className="btn btn-icon pane-toggle"
              aria-label={minimized ? 'Show workspace' : 'Hide workspace'}
              title={minimized ? 'Show workspace' : 'Hide workspace'}
              aria-expanded={!minimized}
              onClick={() => (minimized ? restorePane() : setMinimized(true))}
            >
              <Icon name="panelRight" size={14} />
            </button>
          )}
        </SessionHeading>
      )}
      {narrow && !shared && (
        <span className="m-hidden">
          <SessionArtifacts sessionId={sessionId} />
        </span>
      )}
      {hasWorkspace && <SessionLinks sessionId={sessionId} />}
      {rec?.config.repositories?.length ? (
        <div
          className="flex flex-wrap gap-3 px-4 py-2 text-sm text-muted"
          aria-label="Attached repositories"
        >
          {rec.config.repositories.map((repository) => (
            <span
              className="min-w-0 break-all"
              key={repository.repository}
              title={`/projects/${repository.repository.replace('/', '--')}`}
            >
              {repository.repository}
            </span>
          ))}
        </div>
      ) : null}
      <RepositoryDialog
        open={repositoryOpen && canAttachRepository}
        selected={null}
        onClose={() => setRepositoryOpen(false)}
        onSelect={(repository) => {
          void run('attach-repository', async () => {
            await api.attachRepository(
              sessionId,
              repositoryCheckout(repository),
            )
          })
        }}
      />
      {error && (
        <p role="alert" className="app-space-error">
          {error}
        </p>
      )}
      <Banners />
      {rec?.migration_status === 'diagnostic' && (
        <p role="status" className="px-4 py-3 text-sm text-warn">
          This saved conversation is available to read. It cannot be resumed
          because its earlier history could not be fully migrated.
        </p>
      )}
      {!shared &&
        rec?.migration_status !== 'diagnostic' &&
        (status === 'asleep' || status === 'closed') && (
          <div className="session-sleep-banner">
            <span>This session is asleep. Your conversation is saved.</span>
            <button
              className="btn btn-primary"
              disabled={!!busy}
              onClick={() =>
                void run('resume', () => api.resumeSession(sessionId))
              }
            >
              Resume session
            </button>
          </div>
        )}
      {!shared &&
        rec?.migration_status !== 'diagnostic' &&
        status === 'error' && (
          <div className="session-sleep-banner" role="alert">
            <span>
              Session startup failed. Your queued messages are saved and will
              send after you resume.
            </span>
            <button
              className="btn btn-primary"
              disabled={!!busy}
              onClick={() =>
                void run('resume', () => api.resumeSession(sessionId))
              }
            >
              Retry session
            </button>
          </div>
        )}
      {ui.mode === 'hybrid' && !shared && (
        <div
          className={narrow ? 'm-segmented' : 'session-pane-tabs'}
          role="tablist"
          aria-label="Session views"
        >
          <button
            id="session-chat-tab"
            role="tab"
            tabIndex={tab === 'chat' ? 0 : -1}
            onKeyDown={navigateTab}
            aria-selected={tab === 'chat'}
            aria-controls="session-chat"
            onClick={() => setTab('chat')}
          >
            Chat
          </button>
          <button
            id="session-ui-tab"
            role="tab"
            tabIndex={tab === 'ui' ? 0 : -1}
            onKeyDown={navigateTab}
            aria-selected={tab === 'ui'}
            aria-controls="session-ui"
            onClick={restorePane}
          >
            {app.title}
          </button>
        </div>
      )}
      <div
        ref={row}
        className={`session-panes pane-${ui.position} tab-${shared ? 'chat' : tab}${ui.mode === 'hybrid' && (minimized || shared) ? ' pane-minimized' : ''}`}
        style={
          {
            '--app-pane-width': `${Math.min(80, Math.max(20, width))}%`,
          } as React.CSSProperties
        }
      >
        {showChat && (
          <section id="session-chat" className="session-chat" aria-label="Chat">
            {!shared && !archived && <AcpControls />}
            <Thread
              readOnly={shared || archived}
              bindingId={rec?.config.app_binding_id}
              user={
                rec?.owner_user_id
                  ? {
                      kind: 'user',
                      id: rec.owner_user_id,
                      name: owner?.name,
                      avatarUrl: owner?.avatar_url,
                    }
                  : undefined
              }
              app={{
                kind: 'app',
                id: app.id,
                name: app.title,
                avatarUrl: rec?.config.package_digest
                  ? `/v1/apps/${encodeURIComponent(app.id)}/icon?packageDigest=${encodeURIComponent(rec.config.package_digest)}`
                  : app.iconUrl,
              }}
            />
          </section>
        )}
        {ui.mode === 'hybrid' && !shared && !minimized && ui.resizable && (
          <div
            className="session-divider"
            role="separator"
            aria-label="Resize app pane"
            aria-orientation="vertical"
            aria-valuemin={20}
            aria-valuemax={80}
            aria-valuenow={width}
            tabIndex={0}
            onPointerDown={(event) => {
              event.currentTarget.setPointerCapture(event.pointerId)
              resize(event.clientX)
            }}
            onPointerMove={(event) => {
              if (event.currentTarget.hasPointerCapture(event.pointerId))
                resize(event.clientX)
            }}
            onPointerUp={(event) => {
              if (event.currentTarget.hasPointerCapture(event.pointerId))
                event.currentTarget.releasePointerCapture(event.pointerId)
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                event.preventDefault()
                const delta =
                  (event.key === 'ArrowRight' ? 2 : -2) *
                  (ui.position === 'left' ? 1 : -1)
                setWidth(Math.min(80, Math.max(20, width + delta)))
              }
            }}
          />
        )}
        {showPane && !shared && (
          <section
            id="session-ui"
            className="session-ui"
            aria-label={`${app.title} workspace`}
          >
            {rec?.config.app_binding_id ? (
              <AppPane
                app={versionedApp}
                bindingId={rec.config.app_binding_id}
                sessionId={ui.mode === 'ui' ? null : sessionId}
                startupShareSlug={
                  app.id === 'onepager' &&
                  typeof rec.config.startup_values?.share_slug === 'string'
                    ? rec.config.startup_values.share_slug
                    : undefined
                }
                sessionStarting={
                  snapshot.hydrating ||
                  ['unknown', 'created', 'starting'].includes(status)
                }
              />
            ) : snapshot.hydrating ? (
              <SessionWorkspaceSkeleton title={app.title} />
            ) : (
              <p className="app-space-error">
                The app workspace is unavailable.
              </p>
            )}
          </section>
        )}
        {infoOpen && !narrow && (
          <SessionDetailsPanel
            app={app}
            archiveDisabled={!!busy}
            model={detailsModel}
            onArchive={!implicit && !shared && !archived ? archive : undefined}
            onClose={onToggleInfo}
            record={rec}
            status={status}
          />
        )}
      </div>
      {infoOpen && narrow && (
        <div className="m-details" role="region" aria-label="Session details">
          <SessionDetailsPanel
            app={app}
            archiveDisabled={!!busy}
            model={detailsModel}
            onArchive={!implicit && !shared && !archived ? archive : undefined}
            onClose={onToggleInfo}
            record={rec}
            status={status}
          />
        </div>
      )}
      {!shared && (
        <StatusBar
          configuration={configuration}
          record={record}
          presentation={narrow ? 'sheet' : 'bar'}
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          actions={{
            busy,
            terminal,
            fork: forkPreset,
            sleep: () => void run('sleep', () => api.sleepSession(sessionId)),
            resume: () =>
              void run('resume', () => api.resumeSession(sessionId)),
          }}
        />
      )}
      {narrow && (
        <Sheet open={menuOpen} onClose={() => setMenuOpen(false)} title={title}>
          <RowGroup>
            <Row
              icon="info"
              label="Session details"
              description="Status, app, agent, model, and session ID."
              chevron
              onClick={() => {
                setMenuOpen(false)
                if (!infoOpen) onToggleInfo()
              }}
            />
            <Row
              icon="activity"
              label="Cost ledger"
              description="Tap the amount for the session's cost history."
              right={
                <SessionCost
                  sessionId={sessionId}
                  total={bridge.totalCostUsd}
                />
              }
            />
            {!shared && (
              <Row
                icon="fileText"
                label="Files"
                description="Uploads and generated artifacts."
                chevron
                onClick={() => {
                  setMenuOpen(false)
                  openSessionArtifact({ sessionId })
                }}
              />
            )}
            {canAttachRepository && (
              <Row
                icon="repo"
                label="Add repository"
                description="Clone into this session's sandbox."
                disabled={!!busy}
                onClick={() => {
                  setMenuOpen(false)
                  setRepositoryOpen(true)
                }}
              />
            )}
            {showChat && !shared && (
              <Row
                icon="tool"
                label="Session settings"
                description="Model, permissions, scenario, context"
                chevron
                onClick={() => {
                  setMenuOpen(false)
                  setSettingsOpen(true)
                }}
              />
            )}
            {!implicit && !shared && (
              <Row
                icon="edit"
                label="Rename"
                description="Keeps your name; automatic naming stops."
                onClick={() => {
                  setMenuOpen(false)
                  setRenaming(true)
                }}
              />
            )}
            {showFork && !shared && (
              <Row
                icon="fork"
                label="Fork session"
                description="Continue this work in a new session."
                onClick={() => {
                  setMenuOpen(false)
                  forkPreset()
                }}
              />
            )}
            {rec?.migration_status !== 'diagnostic' && !shared && (
              <Row
                icon={terminal ? 'play' : 'clock'}
                label={terminal ? 'Resume' : 'Sleep'}
                description={
                  terminal
                    ? 'Reconnects the agent to continue.'
                    : 'Pauses the agent; your work is saved.'
                }
                disabled={!!busy}
                onClick={() => {
                  setMenuOpen(false)
                  void run(terminal ? 'resume' : 'sleep', () =>
                    terminal
                      ? api.resumeSession(sessionId)
                      : api.sleepSession(sessionId),
                  )
                }}
              />
            )}
            {!implicit && !shared && (
              <Row
                icon="archive"
                label="Archive session"
                description="Keeps the transcript; hides it from the list."
                disabled={!!busy}
                onClick={() => {
                  setMenuOpen(false)
                  archive()
                }}
              />
            )}
          </RowGroup>
          {shared && <p className="m-hint">Shared with you · read only</p>}
        </Sheet>
      )}
      {renaming && rec && (
        <RenameSession
          session={rec}
          onClose={() => setRenaming(false)}
          onSaved={(saved) => {
            actions.acceptSessionRecord(saved)
            onSessionTitleChanged(saved)
            onSessionsChanged()
          }}
        />
      )}
    </div>
  )
}
function Banners() {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  return (
    <BannersInner
      dismissed={dismissed}
      onDismiss={(k) => setDismissed((s) => new Set(s).add(k))}
    />
  )
}

function BannersInner({
  dismissed,
  onDismiss,
}: {
  dismissed: Set<string>
  onDismiss: (k: string) => void
}) {
  const { snapshot } = useBridge()
  const { acp, bridge } = snapshot
  const items: {
    key: string
    tone: 'warn' | 'danger' | 'info'
    text: string
  }[] = []
  if (bridge.status === 'error' && bridge.error)
    items.push({ key: 'err', tone: 'danger', text: bridge.error })
  if (bridge.status === 'detached')
    items.push({
      key: 'detached',
      tone: 'warn',
      text:
        bridge.error ??
        'The runner no longer hosts this session. Resume to continue.',
    })
  if (bridge.status === 'exited')
    items.push({
      key: 'exited',
      tone: 'info',
      text: 'The agent connection has ended. Resume to continue the conversation.',
    })
  const shown = items.filter((i) => !dismissed.has(i.key))
  if (!shown.length) return null
  return (
    <div className="flex flex-col gap-1 border-b border-line bg-panel px-4 py-2">
      {shown.map((i) => (
        <div
          key={i.key}
          className={`flex items-start gap-2 text-xs ${i.tone === 'danger' ? 'text-danger' : i.tone === 'warn' ? 'text-warn' : 'text-muted'}`}
        >
          <span className="min-w-0 flex-1 break-words">{i.text}</span>
          <button
            className="text-faint hover:text-fg"
            onClick={() => onDismiss(i.key)}
            aria-label="Dismiss"
          >
            <Icon name="close" size={12} />
          </button>
        </div>
      ))}
    </div>
  )
}
